import isObject from 'lodash/isObject';
import each from 'lodash/each';
import keys from 'lodash/keys';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';

import { CarUsage } from '@jerry-serverless/enums/car';
import { OwnershipEnum } from '@jerry-serverless/types';
import type { CarUsageEditKey, InitialCar, CarAdditionalInfo } from '@jerry-serverless/types';

import { DEFAULT_ANNUAL_MILEAGE, mapCarUsageDBValueToFrontendVal } from '../utils/car';

import type { AppData } from './app_data';
import type { MaintenanceReminderConfigInputType } from './maintenance_reminder';
export { OwnershipEnum } from '@jerry-serverless/types';

export { CarUsage, InitialCar, CarUsageEditKey };

export interface CarUsageLabelMileageMap {
  label: string;
  subLabel: number | string;
  annualMileage?: number;
}

// Used to display pre-defined mileage selection blocks.
export const CAR_USAGE_MILEAGE: { [key in CarUsageEditKey]: CarUsageLabelMileageMap } = {
  Pleasure: { label: 'Pleasure', subLabel: 8000, annualMileage: 8000 },
  WorkCommute: { label: 'Commute', subLabel: 10000, annualMileage: 10000 },
  Other: { label: 'Enter custom value', subLabel: 'Other', annualMileage: 8000 },
};

export const CAR_USAGE: Partial<Record<CarUsage, string>> = {
  [CarUsage.Pleasure]: 'Pleasure',
  [CarUsage.WorkCommute]: 'Commute',
};

export const CAR_DAILY_MILEAGE: number[] = [10, 15, 20, 25, 30, 35, 40, 45, 50, 75];
export const CAR_DAILY_MILEAGE2: number[] = [15, 20, 25, 30, 35, 40, 45, 50, 75];

export const OwnershipOptions = [
  { label: 'Owned', value: OwnershipEnum.Owned },
  { label: 'Financed', value: OwnershipEnum.Lien },
  { label: 'Leased', value: OwnershipEnum.Leased },
];

export const CAR_FRAGMENT = `
  id
  isArchived
  year
  make
  model
  submodel
  engine
  vinMasked {
    value
  }
  partialVin
  usage
  annualMileage
  ridesharing
  ownership
  usedForDelivery
  business
  odometer
  additionalInfo {
    isInitialCarDefaultTrim
    vinSource
  }
`;

export interface Car {
  id: string;
  isArchived?: boolean | null;
  year?: string | null;
  make?: string | null;
  model?: string | null;
  submodel?: string | null;
  engine?: string | null;
  vinMasked?: { value: string } | null;
  partialVin?: string | null;
  isPartialVin?: boolean | null;
  usage?: CarUsageEditKey | null;
  annualMileage?: number | null;
  ridesharing?: boolean | null;
  ownership?: OwnershipEnum | null;
  vin?: string | null; // NOTE: VIN can exist locally - we manually set it in 'mergeViewerQueryDataInAppData' using either 'vinMasked.value' or 'partialVin'.
  usedForDelivery?: boolean | null;
  business?: boolean | null;
  licensePlateState?: string | null;
  licensePlateNumber?: string | null;
  odometer?: number | null;
  additionalInfo?: CarAdditionalInfo | null;
}

export interface CarInputType {
  id: string;
  year: string;
  make?: string;
  model?: string;
  submodel?: string;
  engine?: string;
  vin?: string;
  isPartialVin?: boolean;
  usage?: CarUsage;
  annualMileage?: number;
  rideSharing?: boolean;
  ownership?: OwnershipEnum;
  userAdded?: boolean;
  maintenanceReminderConfig?: MaintenanceReminderConfigInputType;
  additionalInfo?: CarAdditionalInfo;
}

export interface InitialCarWithID extends Omit<InitialCar, 'id'> {
  id: string;
}

/**
 * Parse `InitialCar` object from json object.
 *
 * @param obj
 */
export const parseInitialCar = (obj: Record<string, string>, appData?: AppData): InitialCarWithID => {
  const car: InitialCarWithID = {
    id: '',
    year: '',
    make: '',
    model: '',
    submodel: '',
    partialVin: '',
    vin: '',
    annualMileage: DEFAULT_ANNUAL_MILEAGE,
    usage: undefined,
    ridesharing: undefined,
    usedForDelivery: undefined,
    business: undefined,
    licensePlateState: undefined,
    licensePlateNumber: undefined,
    ownership: undefined,
    additionalInfo: undefined,
  };
  if (isObject(obj)) {
    each(keys(car), (key) => {
      if (isString(obj[key]) || isNumber(obj[key]) || isBoolean(obj[key])) {
        let val = obj[key];

        // Map back 'Work Commute' to 'WorkCommute'
        if (key === 'usage' && obj[key] === 'Work Commute') val = 'WorkCommute';
        car[key] = val;
      }
      if (key === 'additionalInfo' && !isEmpty(obj[key])) {
        car[key] = obj[key] as CarAdditionalInfo;
      }
    });
  }
  const usageMapped = mapCarUsageDBValueToFrontendVal(car);
  car.usage = usageMapped[0];
  car.business = usageMapped[1];

  return car;
};
